/**
 * Epic 12 Phase C: Guarded action dialog — risk tiers + typed confirm + reason.
 * Tier 2: typed phrase + reason. Tier 1: reason. Tier 0: confirm only.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
} from '@mui/material';

export type RiskTier = 0 | 1 | 2;

export interface GuardedActionDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  riskTier: RiskTier;
  blastRadiusText: string;
  /** Tier 2: user must type this exact phrase to enable confirm. */
  requiresTypedConfirm?: { phrase: string };
  /** Tier 1/2: require a reason. */
  requiresReason?: boolean;
  /** Body content (warning, description). */
  children?: React.ReactNode;
  confirmLabel?: string;
  confirmColor?: 'primary' | 'warning' | 'error';
  onConfirm: (params: { reason: string }) => void | Promise<void>;
  loading?: boolean;
}

export const GuardedActionDialog: React.FC<GuardedActionDialogProps> = ({
  open,
  onClose,
  title,
  riskTier,
  blastRadiusText,
  requiresTypedConfirm,
  requiresReason = riskTier >= 1,
  children,
  confirmLabel = 'Confirm',
  confirmColor = 'warning',
  onConfirm,
  loading = false,
}) => {
  const [typedPhrase, setTypedPhrase] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setTypedPhrase('');
      setReason('');
    }
  }, [open]);

  const phraseMatch = !requiresTypedConfirm || typedPhrase.trim() === requiresTypedConfirm.phrase;
  const reasonOk = !requiresReason || String(reason || '').trim().length >= 3;
  const canConfirm = phraseMatch && reasonOk && !loading;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    await onConfirm({ reason: String(reason || '').trim() });
    // Parent closes dialog on success; we don't auto-close so errors keep dialog open
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Blast radius:</strong> {blastRadiusText}
          </Typography>
        </Alert>
        {children}
        {requiresTypedConfirm && (
          <TextField
            fullWidth
            size="small"
            label={`Type "${requiresTypedConfirm.phrase}" to confirm`}
            value={typedPhrase}
            onChange={(e) => setTypedPhrase(e.target.value)}
            sx={{ mt: 2 }}
            placeholder={requiresTypedConfirm.phrase}
            error={typedPhrase.length > 0 && !phraseMatch}
          />
        )}
        {requiresReason && (
          <TextField
            fullWidth
            size="small"
            label="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="e.g. Rollback due to post-deploy health failure"
            multiline
            rows={2}
            error={reason.length > 0 && reason.length < 3}
            helperText={requiresReason ? 'Minimum 3 characters' : undefined}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          {loading ? 'Processing…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
